/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect } from 'react';

import { Button, typography2 } from '@coursera/cds-core';
import { SuccessIcon } from '@coursera/cds-icons';
import { type EventData, useTracker } from '@coursera/event-pulse/react';

import type {
  ContractRenewedNotification as ContractRenewedNotificationType,
  Notification as NotificationType,
} from 'bundles/notification-center/types';
import useEnterpriseLearnerContractExtensionGate from 'bundles/unified-home-common/hooks/useEnterpriseLearnerContractExtensionGate';

import _t from 'i18n!nls/notification-center';

const styles = {
  container: css`
    ${typography2.bodyPrimary};
    padding: var(--cds-spacing-150) var(--cds-spacing-100);
    word-break: break-word;
    color: var(--cds-color-interactive-secondary);
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
  `,
  containerRead: css`
    color: var(--cds-color-neutral-primary-weak);
    background-color: var(--cds-color-neutral-background-primary);
  `,
  grid: css`
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: var(--cds-spacing-150);
    align-items: start;
  `,
  iconCell: css`
    display: flex;
    align-items: center;
    align-self: center;
    grid-row: 1;
  `,
  bodyCell: css`
    grid-column: 2;
  `,
  markAsReadButton: css`
    grid-column: 2;
    justify-self: start;
    margin-top: var(--cds-spacing-100);
    padding-left: 0;
  `,
};

type TrackNotificationProps = EventData<'view_notification' | 'click_notification'>;

type Props = {
  notification: ContractRenewedNotificationType;
  rank?: number;
  onClick: (notification: NotificationType) => void;
};

const ContractRenewedNotification = ({ notification, rank, onClick }: Props) => {
  const { messageType, id, isRead } = notification;
  const track = useTracker();
  const isEnterpriseLearnerContractExtensionEnabled = useEnterpriseLearnerContractExtensionGate();

  const eventingData: TrackNotificationProps = {
    notification: {
      templateId: messageType,
      channelType: 'notification_center' as const,
      id,
      rank,
      isPreviouslyRead: isRead,
    },
  };

  useEffect(() => {
    if (!isEnterpriseLearnerContractExtensionEnabled) {
      return;
    }
    track('view_notification', eventingData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification, isEnterpriseLearnerContractExtensionEnabled]);

  const handleMarkAsRead = () => {
    track('click_notification', eventingData);
    onClick(notification);
  };

  if (!isEnterpriseLearnerContractExtensionEnabled) {
    return null;
  }

  return (
    <article
      css={[styles.container, isRead && styles.containerRead]}
      data-testid="contract-renewed-notification"
      aria-labelledby={`contract-renewed-notification-title-${id}`}
    >
      <div css={styles.grid}>
        <div css={styles.iconCell} aria-hidden="true">
          <SuccessIcon color="success" size="medium" />
        </div>
        <strong id={`contract-renewed-notification-title-${id}`}>{_t('Your Coursera access has been extended')}</strong>
        <div css={styles.bodyCell}>
          {_t('Good news — your admin confirmed your access. Keep learning without interruption.')}
        </div>
        {!isRead && (
          <Button
            variant="ghost"
            size="small"
            onClick={handleMarkAsRead}
            css={styles.markAsReadButton}
            data-testid="contract-renewed-notification-mark-as-read"
          >
            {_t('Mark as read')}
          </Button>
        )}
      </div>
    </article>
  );
};

export default ContractRenewedNotification;
